'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Alert, Button, Stack, Text, Title } from '@mantine/core'
import { LuPencil, LuRotateCw } from 'react-icons/lu'

import EmailSentLottie from '@app/components/EmailSentLottie'
import { MAGIC_LINK_RESEND_COOLDOWN_SECONDS } from '@lib/constants'
import { useCountdown } from '@lib/utils/hooks/use-countdown'

type LoginSuccessProps = {
    email: string
    /** Resends the link. Resolves to an error message, or `null` on success. */
    onResend: () => Promise<string | null>
    onChangeEmail: () => void
}

export default function LoginSuccess({ email, onResend, onChangeEmail }: LoginSuccessProps) {
    const t = useTranslations('auth.login.success')
    // A link was just sent, so the cooldown starts already armed.
    const { secondsLeft, isRunning, start } = useCountdown(MAGIC_LINK_RESEND_COOLDOWN_SECONDS)
    const [resending, setResending] = useState(false)
    const [resent, setResent] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleResend = async () => {
        setResending(true)
        setError(null)

        const message = await onResend()

        setResending(false)

        if (message) {
            setResent(false)
            setError(message)
            return
        }

        setResent(true)
        start(MAGIC_LINK_RESEND_COOLDOWN_SECONDS)
    }

    return (
        <Stack align='center' gap='xl' py='md' miw={0}>
            <EmailSentLottie ariaLabel={t('lottieAriaLabel')} />
            <Title
                order={1}
                ta='center'
                m={0}
                fz={{ base: '1.65rem', sm: 'clamp(1.75rem, 4vw, 2.125rem)' }}
                fw={600}
                lh={1.2}
                maw={520}
                style={{ fontFamily: 'var(--eltrust-font-display)' }}>
                {t('title')}
            </Title>
            <Text c='dimmed' ta='center' size='lg' maw={480} lh={1.65} px={{ base: 'xs', sm: 0 }}>
                {t('message', { email })}
            </Text>

            <Stack align='center' gap='sm' w='100%' maw={420}>
                <Text c='dimmed' ta='center' size='sm'>
                    {t('notReceived')}
                </Text>

                {error && (
                    <Alert color='red' variant='light' radius='md' w='100%' role='alert'>
                        {error}
                    </Alert>
                )}

                {resent && (
                    <Text c='green' ta='center' size='sm' role='status'>
                        {t('resent')}
                    </Text>
                )}

                <Button
                    variant='light'
                    size='md'
                    radius='xl'
                    fullWidth
                    leftSection={<LuRotateCw />}
                    loading={resending}
                    disabled={isRunning}
                    onClick={handleResend}>
                    {isRunning ? t('resendCooldown', { seconds: secondsLeft }) : t('resend')}
                </Button>

                <Button variant='subtle' size='sm' radius='xl' leftSection={<LuPencil />} onClick={onChangeEmail}>
                    {t('changeEmail')}
                </Button>
            </Stack>
        </Stack>
    )
}
